import { useFlag } from '@unleash/proxy-client-react';
import { Divider, Switch, Tooltip } from 'antd';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import FullScreenSvg from '../../../../assets/icons/FullScreenSvg';
import QuestionMarkSvg from '../../../../assets/icons/QuestionMarkSvg';
import theme from '../../../../assets/theme';
import {
  DesignerUIStateActions,
  useDesignerUIContext,
} from '../../../../hooks/useDesignerUIState';
import {
  PromptInput,
  SlideType as SlideContentType,
} from '../../../../services/backendService/types';
import { generateRoundOptionColor, toSubjects } from '../../../../utils/creator';
import { generateUniqueId, notUndefined } from '../../../../utils/general';
import { getRoundCategory, hasVoting } from '../../../../utils/roundUtils';
import { MAX_POLL_OPTIONS } from '../../../../utils/templateUtils';
import { zIndexes } from '../../../../utils/zIndexes';
import { Button } from '../../../_atoms/Button';
import { IconWrapper } from '../../../_atoms/IconWrapper';
import { getFirstSlateElementPropsFromSlateString } from '../../../SlideComponents/slateUtil';
import {
  PollOptions,
  Slide,
  SlideElement,
  SlideType,
} from '../../../SlideComponents/slide.types';
import {
  createNewEmptySlide,
  createTextElement,
  findRelatedSlidesWithoutIndex,
  SLIDE_ASPECT,
} from '../../../SlideComponents/slideUtil';
import { TabContainer } from '../../DesignerTabs/DesignerTabUtils';
import {
  DurationSelector,
  LimitResponsesSelector,
  ResultPreview,
  RoundTypeSelector,
  VotingInputs,
} from '../../InteractiveElementsInputComponents/InteractiveElementsInputComponents';
import AiFeedbackImagePromptBuilder from '../AiFeedbackImagePromptBuilder';
import AiFeedbackPromptBuilder from '../AiFeedbackPromptBuilder';
import DrawingBackgroundRow from '../BackgroundTab/DrawingBackgroundRow';

//TODO: rename round varibales to slide
const InteractiveElementsTab = ({
  slide,
  handleRoundTypeChanged,
  onSlideChange,
  rounds,
  isGenerator,
  promptInputs,
  onAllRoundChange,
  setSelectedElements,
  templateId,
}: {
  slide?: Slide;
  handleRoundTypeChanged: (type: SlideType | SlideContentType, replace?: boolean) => void;
  onSlideChange: (round: Slide) => void;
  promptInputs?: PromptInput[];
  rounds: Slide[] | [];
  isGenerator?: boolean;
  onAllRoundChange: (rounds: Slide[], selectedRoundId: string) => void;
  setSelectedElements: (element: SlideElement[] | null) => void;
  templateId: string;
}) => {
  const { t } = useTranslation('Host');
  const isDisabled = useMemo(() => {
    return slide?.type === 'slide';
  }, [slide]);
  const relatedRounds = useMemo(
    () => (slide?.id && rounds ? findRelatedSlidesWithoutIndex(slide.id, rounds) : []),
    [slide, rounds],
  );
  const dispatch = useDesignerUIContext(context => context.dispatch);
  const isAITemplatesEnabled = useFlag('AI_TEMPLATES');
  const handleSwitchToggle = (checked: boolean) => {
    if (!slide) return;
    if (checked) {
      // voting enabled - make last round top 5
      const roundIndex = rounds.findIndex(r => r.id === slide.id);
      const relatedRounds = findRelatedSlidesWithoutIndex(slide.id, rounds);
      const relatedRoundIds = relatedRounds.map(r => r.id);
      const filteredRounds = rounds.filter(r => !relatedRoundIds.includes(r.id));
      const newId = generateUniqueId().toString();
      const newEmptySlide = createNewEmptySlide({ type: 'voting', id: newId });
      const newRound: Slide = {
        ...newEmptySlide,
        dataReferenceSlideId: relatedRounds[0].id,
        durationSeconds: 90,
        durationSecondsConfig: 90,
        responseLimit: 3,
        elements: [
          createTextElement({
            textValue: `${t('Most creative')} ${toSubjects(t, relatedRounds[0].type)}`,
            x: SLIDE_ASPECT.width * 0.05,
            y: SLIDE_ASPECT.height * 0.1,
            lockedFromDeletion: true,
            lockedPosition: true,
            fontSize: 100,
          }),
        ],
      };
      const resultsRound: Slide = {
        ...relatedRounds[relatedRounds.length - 1],
        dataReferenceSlideId: newId,
        type: 'podium',
      };

      const newRounds = [relatedRounds[0], newRound, resultsRound];

      // mutate new array
      filteredRounds.splice(roundIndex, 0, ...newRounds);
      onAllRoundChange(filteredRounds, relatedRounds[0].id);
    } else {
      // from 3 to 2 rounds
      // now voting is disabled - use grid as last round
      const roundIndex = rounds.findIndex(r => r.id === slide.id);
      const relatedRounds = findRelatedSlidesWithoutIndex(slide.id, rounds);
      const relatedRoundIds = relatedRounds.map(r => r.id);
      const filteredRounds = rounds.filter(r => !relatedRoundIds.includes(r.id));

      const newRounds: Slide[] = [
        relatedRounds[0],
        {
          ...relatedRounds[relatedRounds.length - 1],
          dataReferenceSlideId: relatedRounds[0].id,
          type: 'responsegrid',
        },
      ];

      // mutate new array
      filteredRounds.splice(roundIndex, 0, ...newRounds);
      onAllRoundChange(filteredRounds, relatedRounds[0].id);
    }
  };

  const isVotingEnabled = (slideType: SlideType, slideId: string, allRounds: Slide[]) => {
    if (!hasVoting(slideType)) return undefined;
    const relatedSlides = findRelatedSlidesWithoutIndex(slideId, allRounds);
    return !!relatedSlides.find(r => r.type === 'voting');
  };

  const handleTogglePreventVoting = (checked: boolean) => {
    if (!slide) return;
    onSlideChange({ ...slide, preventSkipVotingRound: checked });
    if (!isVotingEnabled(slide.type, slide.id, rounds)) {
      handleSwitchToggle(true);
    }
  };

  const votingEnabled = slide && isVotingEnabled(slide.type, slide.id, rounds);
  const handleVotingRoundChanged = (round: Slide) => {
    onAllRoundChange(
      rounds.map(r => (r.id === round.id ? round : r)),
      relatedRounds[0].id,
    );
  };

  const handleAddPollOption = () => {
    if (!slide) {
      return;
    }
    const options = (slide.pollOptions || []) as PollOptions[];
    if (options.length < MAX_POLL_OPTIONS) {
      const existingColors = options.map(o => o.color).filter(notUndefined);
      const lastOptionSlateObject = getFirstSlateElementPropsFromSlateString(
        options?.[options.length - 1]?.displayName,
      );

      generateRoundOptionColor(existingColors || []);
      const newOption: PollOptions = {
        id: generateUniqueId().toString(),
        displayName: JSON.stringify([
          {
            type: 'paragraph',
            children: [
              {
                text: `Option ${options.length + 1}`,
                color: lastOptionSlateObject?.color || theme.colors.black,
              },
            ],
            align: 'center',
            size: 50,
          },
        ]),
        color: generateRoundOptionColor(existingColors || []),
      };
      const pollOptions = [...options, newOption];
      onSlideChange({ ...slide, pollOptions });
    }
  };

  return (
    <CustomTabContainer style={{ paddingTop: 20 }}>
      {/* //TODO: add translation */}
      {isDisabled && (
        <DisabledOverlay>
          <DisabledTextContainer>
            <h3>
              <span>
                {t('Can not edit activity as it is not an activity slide')}. <br />
                <br />
                {t('To add interactivity to your lesson create a new activity slide')}.
              </span>
            </h3>
          </DisabledTextContainer>
        </DisabledOverlay>
      )}
      <Title $fontSize={20}>{t('Edit activity')}</Title>

      <CustomizeContentContainer>
        {slide && !isDisabled && (
          <>
            <RoundTypeSelector
              roundType={slide.type}
              onRoundTypeSelected={t => {
                handleRoundTypeChanged(t, true);
              }}
            />

            {getRoundCategory(slide.type) === 'response' && (
              <DurationSelector
                title={'Duration'}
                onRoundChange={onSlideChange}
                round={slide}
              />
            )}

            {getRoundCategory(slide.type) === 'response' &&
              slide.type !== 'canvas' &&
              slide.type !== 'aiAssessedAnswers' &&
              slide.type !== 'aiAssessedImageAnswers' &&
              slide.type !== 'poll' && (
                <LimitResponsesSelector onRoundChange={onSlideChange} round={slide} />
              )}
            {getRoundCategory(slide.type) === 'response' && slide.type === 'poll' && (
              <div style={{ paddingTop: 0 }}>
                <Button
                  type="tertiary"
                  styles={{ color: theme.colors.black, borderColor: theme.colors.black }}
                  onClick={handleAddPollOption}
                  disabled={(slide.pollOptions || []).length >= MAX_POLL_OPTIONS}
                >
                  Add poll option
                </Button>
              </div>
            )}

            {slide.type === 'aiAssessedAnswers' && (
              <>
                {isAITemplatesEnabled && (
                  <CollapseButtonContainer
                    style={{ position: 'absolute', top: 10, right: 10 }}
                    onClick={() => {
                      dispatch({
                        type: DesignerUIStateActions.setSelectedTab,
                        payload: {
                          tab: 'editAiFeedback',
                        },
                      });
                    }}
                  >
                    <Tooltip title={'Expand prompt view'}>
                      <CollapseButton>
                        <IconWrapper>
                          <FullScreenSvg />
                        </IconWrapper>
                      </CollapseButton>
                    </Tooltip>
                  </CollapseButtonContainer>
                )}
                <AiFeedbackPromptBuilder
                  onSlideChange={onSlideChange}
                  slide={slide}
                  isGenerator={isGenerator}
                  presentationId={templateId}
                  promptInputs={promptInputs}
                />
              </>
            )}

            {(slide.type === 'canvas' || slide.type === 'aiAssessedImageAnswers') && (
              <DrawingBackgroundRow
                slide={slide}
                onSlideChange={onSlideChange}
                templateId={templateId}
                setSelectedElements={setSelectedElements}
              />
            )}
            {slide.type === 'aiAssessedImageAnswers' && (
              <AiFeedbackImagePromptBuilder
                onRoundChange={onSlideChange}
                slide={slide}
                isGenerator={isGenerator}
                presentationId={templateId}
                promptInputs={promptInputs}
              />
            )}
            {hasVoting(slide.type) && (
              <>
                <Divider type="horizontal" style={{ margin: '10px 0px' }} />

                <StyledFormInputDiv style={{ display: 'flex' }}>
                  <div style={{ display: 'flex', alignItems: 'center', marginRight: 10 }}>
                    <Switch checked={votingEnabled} onChange={handleSwitchToggle} />
                  </div>
                  <p style={{ margin: 0, fontSize: 14 }}>
                    Students will {!votingEnabled ? ' not ' : ''}vote
                    <Tooltip
                      title={
                        'Add voting to let students vote and see the results on a podium. Or turn off voting to display the results in a grid'
                      }
                    >
                      <TooltipIconWrapper>
                        <QuestionMarkSvg />
                      </TooltipIconWrapper>
                    </Tooltip>
                  </p>
                </StyledFormInputDiv>
              </>
            )}
            {votingEnabled && isGenerator && hasVoting(slide.type) && (
              <>
                <StyledFormInputDiv style={{ display: 'flex' }}>
                  <div style={{ display: 'flex', alignItems: 'center', marginRight: 10 }}>
                    <Switch
                      checked={slide.preventSkipVotingRound ? true : false}
                      onChange={handleTogglePreventVoting}
                    />
                  </div>
                  <p style={{ margin: 0, fontSize: 14 }}>
                    {slide.preventSkipVotingRound
                      ? `Voting is mandatory`
                      : `Voting is optional`}
                    <Tooltip
                      title={
                        'Turning this on will remove the option for teachers to skip voting (the voting round is mandatory), when it is off teachers can skip the voting round, if desired.'
                      }
                    >
                      <TooltipIconWrapper>
                        <QuestionMarkSvg />
                      </TooltipIconWrapper>
                    </Tooltip>
                  </p>
                </StyledFormInputDiv>
              </>
            )}

            {votingEnabled && (
              <VotingInputs
                handleVotingRoundChanged={handleVotingRoundChanged}
                relatedRounds={relatedRounds}
              />
            )}

            {getRoundCategory(slide.type) === 'response' && (
              <>
                <Divider type="horizontal" style={{ margin: '10px 0px' }} />
                <ResultPreview roundType={slide.type} votingEnabled={votingEnabled} />
              </>
            )}
          </>
        )}
      </CustomizeContentContainer>
    </CustomTabContainer>
  );
};

export default InteractiveElementsTab;

const CustomTabContainer = styled(TabContainer)`
  padding-top: 20px;
  padding: 20px 0px 0px 0px;
`;

const Title = styled.h3<{ $fontSize: number }>`
  text-align: left;
  width: 100%;
  font-weight: bold;
  font-size: ${({ $fontSize }) => $fontSize}px;
  line-height: 140%;
  color: ${theme.colors.greyVeryDark};
  display: flex;
  align-items: center;
  padding: 0px 20px;

  @media (max-width: 690px) {
    font-size: ${({ $fontSize }) => $fontSize - 5}px;
  }
`;

const TooltipIconWrapper = styled.span`
  margin-left: 10px;
  &:hover {
    cursor: pointer;
  }
  svg {
    height: 16px;
    width: 16px;
    color: ${theme.colors.black};
  }
`;

const CollapseButtonContainer = styled.div`
  padding: 0;
  border: none;
  justify-content: center;
  display: flex;
  align-items: center;
  cursor: pointer;
  margin-left: 10px;
  z-index: ${zIndexes('CREATOR_LEFT_BAR_HIDE')};
  svg {
    padding: 0;
  }
`;

const CollapseButton = styled.div`
  background-color: white;
  height: 40px;
  width: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 5px;
  border-radius: 10px;
  box-shadow: ${theme.effects.floatingCreatorElementShadow};
  svg {
    padding: 0;
    width: 20px !important;
    height: 20px !important;
  }
`;

const CustomizeContentContainer = styled.div`
  height: calc(100% - 38px);
  width: 100%;
  overflow-y: auto;
  padding: 10px 20px 0px 20px;
  @media (max-width: 900px) {
    padding-bottom: 100px;
  }
`;

const StyledFormInputDiv = styled.div`
  margin-bottom: 20px;
  max-width: 100%;
  p {
    margin: 20px auto 5px auto;
    font-weight: bold;
    color: #282f33;
    font-size: 20px;
  }
`;

const DisabledTextContainer = styled.div`
  padding: 20px 30px;
  background: rgba(51, 160, 221, 0.8);
  border-radius: 10px;
  box-shadow: 0px 2px 20px rgba(0, 0, 0, 0.1);
  & h3 {
    color: white;
    font-weight: bold;
  }
`;

const DisabledOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  background-color: rgba(255, 255, 255, 0.8);
  z-index: ${zIndexes('CREATOR_MEDIA_DISABLED_OVERLAY')};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
`;
